import { List } from 'immutable';
import isEqual from 'lodash/isEqual';
import React, { PureComponent, ReactNode } from 'react';
import request from 'app/utils/request';
import { ALL_ENUM_DATA } from './enumData';
import { IBareEnum, IRawEnum, IEnumData, RawEnum } from './EnumClass';
import appConfig from 'app/config/client';
import EXERCISE_TYPES from 'app/constants/ExerciseTypes';
import { MEDIA_SECTION_SIZES } from 'app/constants/MediaSizes';
import Button from 'app/components/forms/Button';
import { ALL_TEACHING_SKILLS } from 'app/constants/TeachingSkills';
import { RunningIn } from 'app/config/types/DeployInfo';
import { warn } from 'app/services/logger';
import { getBackendPath } from 'app/utils/paths';
import * as t from 'io-ts';
import { decodeOrThrow } from 'app/utils/ioTs';
import omit from 'lodash/omit';

export const ServerCheckEnabledContext = React.createContext<boolean>(
  appConfig.deployInfo.runningIn !== RunningIn.Production);

const MANUAL_ENUMS: { [key: string]: List<IBareEnum<string>> } = {
  media_sizes: MEDIA_SECTION_SIZES,
  exercise_types: EXERCISE_TYPES.toList(),
  teaching_skills: ALL_TEACHING_SKILLS,
};

enum MismatchType {
  Manual,
  Generated,
  Exports,
  BadServerResponse,
}

class Mismatch {
  public constructor(public readonly typeName: string, public readonly mismatchType: MismatchType) {}
}

const TypesApiResponse = t.interface({
  data: t.array(RawEnum),
});

async function getTypeData(endpoint: string): Promise<Array<IRawEnum<string>>> {
  const response = await request({
    method: 'GET',
    timeout: 0,
    url: `${getBackendPath()}/api/types/${endpoint}`,
  });

  return decodeOrThrow(TypesApiResponse, response).data;
}

async function checkManualEnums(typeName: string, localData: List<IBareEnum<string>>) {
  let remoteData;
  try {
    remoteData = await getTypeData(typeName);
  } catch (err) {
    return new Mismatch(typeName, MismatchType.BadServerResponse);
  }

  const ids = remoteData
    .map((e) => e.enum_constant)
    .sort();

  const localIds = localData
    .map((d) => d.id)
    .sort()
    .toJS();

  if (!isEqual(localIds, ids)) {
    return new Mismatch(typeName, MismatchType.Manual);
  }
}

function rawEnumComparator(a: Pick<IRawEnum<string>, 'enum_constant'>, b: Pick<IRawEnum<string>, 'enum_constant'>) {
  if (a.enum_constant < b.enum_constant) {
    return -1;
  } else if (a.enum_constant > b.enum_constant) {
    return 1;
  } else {
    return 0;
  }
}

async function checkGeneratedEnums(localData: IEnumData<string>) {
  const { raw, list, endpoint, omitDataFields } = localData;

  let response;
  try {
    response = (await getTypeData(endpoint)).map(({ enum_constant, ...rest }) => ({
      enum_constant,
      ...omit(rest, omitDataFields),
    }));
  } catch (err) {
    return new Mismatch(endpoint, MismatchType.BadServerResponse);
  }

  const items = response.sort(rawEnumComparator);
  const localItems = raw.sort(rawEnumComparator);

  if (!isEqual(items, localItems)) {
    return new Mismatch(endpoint, MismatchType.Generated);
  }

  const ids = items.map((e: IRawEnum<string>) => e.enum_constant);
  const localIds = list
    .map((d: IBareEnum<string>) => d.id)
    .sort()
    .toJS();

  if (!isEqual(ids, localIds)) {
    return new Mismatch(endpoint, MismatchType.Exports);
  }
}

async function performCheck() {
  const promises = [
    ...Object.keys(ALL_ENUM_DATA).map((key) =>
      checkGeneratedEnums(ALL_ENUM_DATA[key]),
    ),
    ...Object.keys(MANUAL_ENUMS).map((key) =>
      checkManualEnums(key, MANUAL_ENUMS[key]),
    ),
  ];
  const errors = await Promise.all(promises);
  return errors.filter((error) => error !== undefined) as Mismatch[];
}

interface IProps {
  children: ReactNode;
}

interface IDeps {
  checkEnabled: boolean;
}

interface IState {
  mismatches: Mismatch[];
}

export class ServerCheck extends PureComponent<IProps & IDeps, IState> {
  public constructor(props: IProps & IDeps) {
    super(props);
    this.state = { mismatches: [] };
  }
  public componentDidMount() {
    void (async () => {
      if (this.props.checkEnabled) {
        try {
          const mismatches = await performCheck();
          this.setState({ mismatches });
        } catch (e) {
          warn(e);
        }
      }
    })();
  }
  public render() {
    const { mismatches } = this.state;

    if (this.props.checkEnabled && mismatches.length > 0) {
      return this.renderMismatches();
    } else {
      return this.props.children;
    }
  }

  private renderMismatches() {
    const manualMismatches = this.mismatchesForType(MismatchType.Manual);
    const generatedMismatches = this.mismatchesForType(MismatchType.Generated);
    const exportMismatches = this.mismatchesForType(MismatchType.Exports);
    const responseMismatches = this.mismatchesForType(MismatchType.BadServerResponse);
    return (
      <div>
        <h1>Enums Mismatch</h1>
        { generatedMismatches.length > 0 && (
          <>
            <p>
              The following enums do not match the server and need to be regenerated.
              Please run <code>yarn generate:enums</code>.
            </p>
            <ul>{generatedMismatches.map(({ typeName }) => <li key={typeName}>{typeName}</li>)}</ul>
            <p>
              After generation make sure to add any necessary translations
              to <code>config/locales/en.json</code>.
            </p>
            <p>
              New assignable abilities should be added to one of the ability
              groups in <code>app/constants/enums/Ability/index.ts</code>.
            </p>
          </>
        )}
        { exportMismatches.length > 0 && (
          <>
            <p>
              The following enums are up to date with the server but the
              exports do not match. This usually means they are being modified
              prior to being exported.
              Check <code>index.ts</code> or <code>main.ts</code> of the enum.
            </p>
            <ul>{exportMismatches.map(({ typeName }) => <li key={typeName}>{typeName}</li>)}</ul>
          </>
        )}
        { manualMismatches.length > 0 && (
          <>
            <p>The following enums have to be manually updated:</p>
            <ul>{manualMismatches.map(({ typeName }) => <li key={typeName}>{typeName}</li>)}</ul>
          </>
        )}
        { responseMismatches.length > 0 && (
          <>
            <p>The server errored querying the following enums. Ensure they still exist.
              You may have to run <code>yarn generate:enums</code>.
            </p>
            <ul>{responseMismatches.map(({ typeName }) => <li key={typeName}>{typeName}</li>)}</ul>
          </>
        )}
        <Button onClick={this.clear}>Continue anyway</Button>
      </div>
    );
  }

  private clear = () => {
    this.setState({ mismatches: [] });
  };

  private mismatchesForType(type: MismatchType) {
    return this.state.mismatches.filter(({ mismatchType }) =>
      type === mismatchType,
    );
  }
}

const ServerCheckInjectDependencies = (props: IProps) => (
  <ServerCheckEnabledContext.Consumer>{(serverCheckEnabled) =>
    <ServerCheck
      {...props}
      checkEnabled={serverCheckEnabled}
    />
  }</ServerCheckEnabledContext.Consumer>
);

export default ServerCheckInjectDependencies;
